/**
 * Channel sharing (section 43).
 *
 * A channel has exactly one homeserver, and only that server decides what a
 * partner may do in it. So this tab has two shapes: the editable one, for a
 * channel this server owns, and a read-only account of what was granted, for a
 * channel that belongs to somebody else. Showing the second as a disabled copy
 * of the first would suggest the permissions are ours to argue with.
 */

import { useMemo, useRef, useState } from 'react'
import type { ChangeEvent, ReactElement } from 'react'
import { Link } from 'react-router-dom'

import { isApiError } from '@/api/client'
import { usePeers, useRevokeShare, useShares, useUpdateShare, useShareRoom } from '@/api/queries'
import type { Room, RoomShare, RoomSharePermissions, RoomShareStatus } from '@/api/types'
import { Badge, Button, Checkbox, Modal, Spinner, Select, useToast } from '@/components/ui'
import type { BadgeTone } from '@/components/ui'
import { formatDay } from '@/lib/format'
import panel from './DetailsPanel.module.css'
import styles from './RoomSharing.module.css'

interface PermissionSpec {
  key: keyof RoomSharePermissions
  label: string
  hint: string
}

const PERMISSIONS: readonly PermissionSpec[] = [
  { key: 'read', label: 'Read', hint: 'See the channel and everything posted in it.' },
  { key: 'post', label: 'Post', hint: 'Write messages that everyone here receives.' },
  { key: 'upload', label: 'Upload files', hint: 'Attach files, which are stored on this server.' },
  {
    key: 'invite_users',
    label: 'Invite own users',
    hint: 'Bring more of their own colleagues into the channel, without asking.',
  },
  {
    key: 'share_further',
    label: 'Share further',
    hint: 'Pass this channel on to a third server. Off unless you mean it: the servers it reaches next are not ones you approve.',
  },
]

const DEFAULT_PERMISSIONS: RoomSharePermissions = {
  read: true,
  post: true,
  upload: true,
  invite_users: false,
  share_further: false,
}

const STATUS_TONES: Record<RoomShareStatus, BadgeTone> = {
  invited: 'info',
  active: 'success',
  rejected: 'danger',
  revoked: 'neutral',
}

const STATUS_LABELS: Record<RoomShareStatus, string> = {
  invited: 'Invitation sent',
  active: 'Active',
  rejected: 'Declined',
  revoked: 'Revoked',
}

function describe(error: unknown, fallback: string): string {
  if (isApiError(error)) return error.message
  if (error instanceof Error && error.message !== '') return error.message
  return fallback
}

/** Compared by value, so a refetch that changed nothing does not discard an edit. */
function signature(permissions: RoomSharePermissions): string {
  return PERMISSIONS.map((spec) => (permissions[spec.key] ? '1' : '0')).join('')
}

function withPermission(
  current: RoomSharePermissions,
  key: keyof RoomSharePermissions,
  value: boolean,
): RoomSharePermissions {
  const next: RoomSharePermissions = { ...current }
  next[key] = value
  return next
}

// ── One share ───────────────────────────────────────────────────────────────

interface ShareCardProps {
  share: RoomShare
  roomId: string
  channelName: string
  canManage: boolean
}

function ShareCard({ share, roomId, channelName, canManage }: ShareCardProps): ReactElement {
  const toast = useToast()
  const updateShare = useUpdateShare()
  const revokeShare = useRevokeShare()

  const [draft, setDraft] = useState<RoomSharePermissions>(share.permissions)
  const [revoking, setRevoking] = useState(false)

  // Deriving state from a prop during render is the sanctioned way to follow a
  // value that arrives asynchronously without an effect and an extra paint.
  const stored = signature(share.permissions)
  const lastStored = useRef(stored)
  if (lastStored.current !== stored) {
    lastStored.current = stored
    setDraft(share.permissions)
  }

  const dirty = signature(draft) !== stored

  const save = (): void => {
    updateShare.mutate(
      { roomId, shareId: share.id, changes: { permissions: draft } },
      {
        onSuccess: () => toast.success(`Permissions for ${share.peer_server_id} were saved.`),
        onError: (error) => toast.error(describe(error, 'The permissions could not be saved.')),
      },
    )
  }

  const confirmRevoke = (): void => {
    revokeShare.mutate(
      { roomId, shareId: share.id },
      {
        onSuccess: () => {
          toast.success(`${share.peer_server_id} no longer has access.`)
          setRevoking(false)
        },
        onError: (error) => toast.error(describe(error, 'The share could not be revoked.')),
      },
    )
  }

  return (
    <li className={styles.share}>
      <div className={styles.shareTop}>
        <span className={panel.rowName}>
          <span className={panel.rowNameText}>{share.peer_server_id}</span>
          <Badge tone={STATUS_TONES[share.status]}>{STATUS_LABELS[share.status]}</Badge>
        </span>
      </div>
      <p className={panel.rowMeta}>{`Shared ${formatDay(share.created_at)}`}</p>

      <fieldset className={styles.permissions} disabled={!canManage || updateShare.isPending}>
        <legend className={panel.sectionTitle}>{`What ${share.peer_server_id} may do`}</legend>
        {PERMISSIONS.map((spec) => (
          <Checkbox
            key={spec.key}
            className={spec.key === 'share_further' && draft.share_further ? styles.consequential : undefined}
            label={spec.label}
            hint={spec.hint}
            checked={draft[spec.key]}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              const value = event.target.checked
              setDraft((current) => withPermission(current, spec.key, value))
            }}
          />
        ))}
      </fieldset>

      {canManage ? (
        <div className={styles.shareActions}>
          <Button variant="ghost" size="sm" onClick={() => setRevoking(true)}>
            Revoke access
          </Button>
          <div className={panel.rowActions}>
            {dirty ? (
              <Button variant="secondary" size="sm" onClick={() => setDraft(share.permissions)}>
                Discard
              </Button>
            ) : null}
            <Button variant="primary" size="sm" disabled={!dirty} loading={updateShare.isPending} onClick={save}>
              Save
            </Button>
          </div>
        </div>
      ) : null}

      <Modal
        open={revoking}
        onClose={() => setRevoking(false)}
        title="Revoke access"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setRevoking(false)}>
              Cancel
            </Button>
            <Button variant="danger" loading={revokeShare.isPending} onClick={confirmRevoke}>
              Revoke
            </Button>
          </>
        }
      >
        <div className={panel.confirmText}>
          <p>
            {`Everyone on ${share.peer_server_id} loses ${channelName} immediately: they stop receiving new messages, they cannot post, and the channel disappears from their client.`}
          </p>
          <p>
            {`Messages that already reached ${share.peer_server_id} stay on that server — revoking cannot reach back and delete them. Files hosted here stop being downloadable.`}
          </p>
        </div>
      </Modal>
    </li>
  )
}

// ── Choosing a peer ─────────────────────────────────────────────────────────

interface PeerPickerProps {
  roomId: string
  alreadyShared: ReadonlySet<string>
}

/**
 * Mounted only for an administrator: the peer list is an admin endpoint, and
 * asking for it as anybody else buys a guaranteed 403.
 */
function PeerPicker({ roomId, alreadyShared }: PeerPickerProps): ReactElement {
  const toast = useToast()
  const { data: peers, isLoading, error } = usePeers()
  const shareRoom = useShareRoom()
  const [chosen, setChosen] = useState('')

  const options = useMemo(
    () =>
      (peers ?? [])
        .filter(
          (peer) =>
            peer.status === 'active' &&
            peer.permissions.share_rooms &&
            !alreadyShared.has(peer.server_id),
        )
        .map((peer) => ({
          value: peer.server_id,
          label:
            peer.display_name === null || peer.display_name === ''
              ? peer.server_id
              : `${peer.display_name} (${peer.server_id})`,
        })),
    [peers, alreadyShared],
  )

  const share = (): void => {
    if (chosen === '') return
    shareRoom.mutate(
      { roomId, body: { peer_server_id: chosen, permissions: DEFAULT_PERMISSIONS } },
      {
        onSuccess: () => {
          toast.success(`An invitation was sent to ${chosen}.`)
          setChosen('')
        },
        onError: (failure) => toast.error(describe(failure, 'The channel could not be shared.')),
      },
    )
  }

  if (error !== null) {
    return (
      <p className={panel.error} role="alert">
        {describe(error, 'The list of partner servers could not be loaded.')}
      </p>
    )
  }

  if (isLoading) {
    return (
      <div className={panel.loading}>
        <Spinner size="sm" label="Loading partner servers" />
      </div>
    )
  }

  if (options.length === 0) {
    return (
      <div className={styles.peerEmpty}>
        <p>No approved servers available.</p>
        <Link className={styles.adminLink} to="/admin/federation">
          Manage servers →
        </Link>
      </div>
    )
  }

  return (
    <div className={styles.picker}>
      <Select
        label="Share with"
        placeholder="Choose a server"
        value={chosen}
        options={options}
        onChange={(event: ChangeEvent<HTMLSelectElement>) => setChosen(event.target.value)}
      />
      <div className={styles.pickerActions}>
        <Link className={styles.adminLink} to="/admin/federation">
          Manage servers
        </Link>
        <Button
          size="sm"
          variant="primary"
          disabled={chosen === ''}
          loading={shareRoom.isPending}
          onClick={share}
        >
          Share
        </Button>
      </div>
    </div>
  )
}

// ── A channel somebody else owns ────────────────────────────────────────────

function GrantedElsewhere({ room, share }: { room: Room; share: RoomShare | undefined }): ReactElement {
  return (
    <div className={panel.stack}>
      <section className={panel.section}>
        <h3 className={panel.sectionTitle}>Home server</h3>
        <p className={panel.sectionHint}>
          {`This channel belongs to ${room.owner_server_id}. It assigns every message its place in the history, and it decides what this server may do here — so none of it can be changed from this side.`}
        </p>
        <dl className={panel.facts}>
          <dt className={panel.factLabel}>Owned by</dt>
          <dd className={panel.factValue}>{room.owner_server_id}</dd>
          {share !== undefined ? (
            <>
              <dt className={panel.factLabel}>Status</dt>
              <dd className={panel.factValue}>{STATUS_LABELS[share.status]}</dd>
              <dt className={panel.factLabel}>Shared</dt>
              <dd className={panel.factValue}>{formatDay(share.created_at)}</dd>
            </>
          ) : null}
        </dl>
      </section>

      <section className={panel.section}>
        <h3 className={panel.sectionTitle}>What this server was granted</h3>
        {share === undefined ? (
          <p className={panel.sectionHint}>
            {`The grant is held by ${room.owner_server_id} and is not published to this side. What you can do in the channel is what it lets through.`}
          </p>
        ) : (
          <ul className={styles.grants}>
            {PERMISSIONS.map((spec) => (
              <li key={spec.key} className={styles.grant}>
                <span className={styles.grantLabel}>{spec.label}</span>
                <Badge tone={share.permissions[spec.key] ? 'success' : 'neutral'}>
                  {share.permissions[spec.key] ? 'Allowed' : 'Not allowed'}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}

// ── The tab ─────────────────────────────────────────────────────────────────

export interface RoomSharingProps {
  room: Room
  canManage: boolean
  /** Picking a new partner reads the peer list, which only an admin may do. */
  canChoosePeers: boolean
}

export function RoomSharing({ room, canManage, canChoosePeers }: RoomSharingProps): ReactElement {
  const { data: shares, isLoading, error } = useShares(room.id)

  const channelName = room.name === null ? 'this channel' : `#${room.name}`

  const alreadyShared = useMemo(
    () =>
      new Set(
        (shares ?? [])
          .filter((share) => share.status !== 'revoked' && share.status !== 'rejected')
          .map((share) => share.peer_server_id),
      ),
    [shares],
  )

  if (!room.is_local) {
    return <GrantedElsewhere room={room} share={(shares ?? [])[0]} />
  }

  if (error !== null) {
    return (
      <p className={panel.error} role="alert">
        {describe(error, 'The sharing settings could not be loaded.')}
      </p>
    )
  }

  if (isLoading || shares === undefined) {
    return (
      <div className={panel.loading}>
        <Spinner label="Loading sharing" />
      </div>
    )
  }

  return (
    <div className={styles.overview}>
      <div className={styles.summary}>
        <p className={styles.summaryTitle}>
          {shares.length === 0
            ? 'Not shared'
            : `Shared with ${shares.length} ${shares.length === 1 ? 'server' : 'servers'}`}
        </p>
        <p className={styles.summaryHint}>
          {shares.length === 0
            ? 'Only people on this server have access.'
            : 'Connected servers have the permissions shown below.'}
        </p>
      </div>

      {shares.length > 0 ? (
        <ul className={panel.list}>
          {shares.map((share) => (
            <ShareCard
              key={share.id}
              share={share}
              roomId={room.id}
              channelName={channelName}
              canManage={canManage}
            />
          ))}
        </ul>
      ) : null}

      {canManage ? (
        canChoosePeers ? (
          <PeerPicker roomId={room.id} alreadyShared={alreadyShared} />
        ) : (
          <p className={styles.summaryHint}>Ask an administrator to share this channel.</p>
        )
      ) : null}
    </div>
  )
}

export default RoomSharing
